import type React from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { createBrowserRouter, matchRoutes, RouterProvider } from 'react-router-dom'
import './index.css'
import { routes } from './routes'
import { isSubdomainContext } from './hooks/useSubdomainContext'

// captureUTMCookie() moved to Root.tsx useEffect (Phase 3) — not called at module top

// Resolve lazy modules for the routes matching the current URL BEFORE hydrating.
// Without this, the prerendered HTML hydrates against an unresolved (empty) lazy
// route → mismatch → React discards the SSR tree and re-renders, duplicating
// content. Pre-loading the matched route makes its Component available
// synchronously at hydrateRoot time. Navigation to other routes still
// code-splits via their own lazy loaders.
async function hydrate() {
  const lazyMatches = matchRoutes(routes, window.location)?.filter(
    (m) => m.route.lazy,
  )

  if (lazyMatches && lazyMatches.length > 0) {
    await Promise.all(
      lazyMatches.map(async (match) => {
        const loader = match.route.lazy as () => Promise<{ Component: React.ComponentType }>
        const routeModule = await loader()
        Object.assign(match.route, { ...routeModule, lazy: undefined })
      }),
    )
  }

  const router = createBrowserRouter(routes)
  const container = document.getElementById('root')!
  const app = <RouterProvider router={router} />
  // Workspace hosts (custom domain/subdomain) are served the prerendered Foxia
  // landing HTML, but render the workspace lobby. Hydrating would mismatch, so
  // render client-side from scratch, discarding the prerendered markup.
  if (isSubdomainContext()) {
    createRoot(container).render(app)
    return
  }
  hydrateRoot(container, app)
}

hydrate()
